/* biome-ignore-all lint/suspicious/noExplicitAny: dynamic log inspection */
import { Drawer, Descriptions, Tag, Button, Alert, message } from "antd"
import { CopyOutlined, BugOutlined } from "@ant-design/icons"
import { DeviceBadge } from "@/components/DeviceBadge"
import type React from "react"
import type { SystemLogItem } from "@/services/log/systemLogApi"
import JsonView from "@uiw/react-json-view"
import { lightTheme } from "@uiw/react-json-view/light"
import dayjs from "dayjs"

interface SystemLogDetailDrawerProps {
  open: boolean
  record: SystemLogItem | null
  onClose: () => void
  serviceName?: string
}

export const SystemLogDetailDrawer: React.FC<SystemLogDetailDrawerProps> = ({
  open,
  record,
  onClose,
  serviceName = "Dịch vụ",
}) => {
  if (!record) return null

  const level = record.level || 30
  const isError = level >= 50
  const isWarn = level === 40

  const getLevelBadge = () => {
    if (level >= 60) return <Tag color="#780650">FATAL (60)</Tag>
    if (level >= 50) return <Tag color="error">ERROR ({level})</Tag>
    if (level >= 40) return <Tag color="warning">WARN ({level})</Tag>
    if (level >= 30) return <Tag color="processing">INFO ({level})</Tag>
    return <Tag color="purple">DEBUG ({level})</Tag>
  }

  const handleCopyStack = () => {
    if (record.err?.stack) {
      navigator.clipboard.writeText(record.err.stack)
      message.success("Đã sao chép Stack Trace vào clipboard!")
    }
  }

  const handleCopyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(record, null, 2))
    message.success("Đã sao chép toàn bộ dữ liệu log JSON!")
  }

  const statusCode = record.err?.statusCode || record.res?.statusCode || record.statusCode

  return (
    <Drawer
      title={
        <div className="flex items-center justify-between pr-6">
          <div className="flex items-center gap-2">
            <BugOutlined className={isError ? "text-lg text-red-500" : "text-blue-500 text-lg"} />
            <span className="font-bold text-base text-gray-900">Chi tiết Log {serviceName}</span>
            {getLevelBadge()}
          </div>
          {statusCode && (
            <Tag color={statusCode >= 500 ? "error" : statusCode >= 400 ? "warning" : "success"} className="font-mono">
              HTTP {statusCode}
            </Tag>
          )}
        </div>
      }
      open={open}
      onClose={onClose}
      width={800}
      destroyOnClose
    >
      <div className="flex flex-col gap-5 pb-6">
        {/* Alert box thông điệp lỗi */}
        <Alert
          type={isError ? "error" : isWarn ? "warning" : "info"}
          showIcon
          message={
            <span className="font-bold text-sm">{record.err?.type || record.err?.name || "Thông báo hệ thống"}</span>
          }
          description={
            <div className="mt-1 break-words font-mono text-xs">{record.err?.message || record.msg || record.raw}</div>
          }
          className="rounded-lg"
        />

        {/* Thông tin metadata */}
        <div>
          <div className="mb-2 font-bold text-gray-500 text-xs uppercase tracking-wide">Thông tin cơ bản</div>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Thời gian thực hiện">
              <span className="font-mono text-xs">
                {record.time ? dayjs(record.time).format("DD/MM/YYYY HH:mm:ss.SSS") : "—"}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Cấp độ log (Pino Level)">
              <span className="font-mono font-semibold">{level}</span>
            </Descriptions.Item>
            {statusCode && (
              <Descriptions.Item label="Mã lỗi HTTP">
                <span className="font-bold font-mono text-red-600">{statusCode}</span>
              </Descriptions.Item>
            )}
            {record.err?.type && (
              <Descriptions.Item label="Loại ngoại lệ (Error Type)">
                <span className="font-mono font-semibold text-rose-600">{record.err.type}</span>
              </Descriptions.Item>
            )}
            {record.req?.method && (
              <Descriptions.Item label="Request HTTP" span={2}>
                <span className="font-mono text-xs">
                  <Tag color="blue">{record.req.method}</Tag> {record.req.url}
                </span>
              </Descriptions.Item>
            )}
            {record.req?.headers?.["user-agent"] && (
              <Descriptions.Item label="Thiết bị / Nền tảng" span={2}>
                <DeviceBadge userAgent={record.req.headers["user-agent"]} showDetails />
              </Descriptions.Item>
            )}
            {record.req?.remoteAddress && (
              <Descriptions.Item label="Địa chỉ IP Client" span={2}>
                <span className="font-mono text-xs">{record.req.remoteAddress}</span>
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>

        {/* Khối Stack Trace (nếu có lỗi) */}
        {record.err?.stack && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1.5 font-bold text-red-600 text-xs uppercase tracking-wide">
                <BugOutlined /> Vết ngăn xếp (Stack Trace)
              </span>
              <Button size="small" icon={<CopyOutlined />} onClick={handleCopyStack}>
                Sao chép Stack Trace
              </Button>
            </div>
            <div className="max-h-72 overflow-x-auto rounded-lg border border-red-950/40 bg-gray-950 p-3.5 font-mono text-red-200 text-xs leading-relaxed">
              <pre className="whitespace-pre-wrap">{record.err.stack}</pre>
            </div>
          </div>
        )}

        {/* JSON Payload thô */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="font-bold text-gray-500 text-xs uppercase tracking-wide">
              Dữ liệu JSON gốc (Raw Payload)
            </span>
            <Button size="small" icon={<CopyOutlined />} onClick={handleCopyJson}>
              Sao chép JSON
            </Button>
          </div>
          <div className="max-h-96 overflow-auto rounded-lg border border-gray-200 bg-white p-3 font-mono text-xs">
            <JsonView value={record} collapsed={2} style={lightTheme} />
          </div>
        </div>
      </div>
    </Drawer>
  )
}

export default SystemLogDetailDrawer
